import React, { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { type Client, type InsertClient } from "@shared/schema";
import { ClientForm } from "@/components/client-form";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/ui/page-header";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Plus, Pencil, Trash2, AlertTriangle, AlertCircle, Share, Link, Mail, FileText, FolderKanban } from "lucide-react";
import { useLocation } from "wouter";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { formatMoney, formatTax } from "@/lib/format-helpers";

export default function Clients() {
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [deletingClient, setDeletingClient] = useState<Client | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "email" | "hourlyRate">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const { toast } = useToast();
  const { t } = useTranslation(['clients', 'common']);
  const { user } = useAuth();
  const [, setLocation] = useLocation();
  
  // Ak nie je user, zobraz fallback
  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="p-4 text-sm text-muted-foreground">Redirecting...</div>
      </div>
    );
  }
  
  // Neprepíname jazyk lokálne – rešpektujeme globálne i18n nastavenie

  const { data: clients, isLoading, error } = useQuery<Client[]>({
    queryKey: ["/api/clients"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/clients");
      const data = await res.json();
      console.log('🔧 [clients.tsx] GET clients response:', data);
      // API vracia buď objekt s items alebo pole
      const clientList = Array.isArray(data) ? data : data.items || [];
      console.log('🔧 [clients.tsx] Processed clients:', clientList);
      if (clientList && clientList.length > 0) {
        console.log('🔧 [clients.tsx] First client full data:', JSON.stringify(clientList[0], null, 2));
        console.log('🔧 [clients.tsx] First client keys:', Object.keys(clientList[0]));
        console.log('🔧 [clients.tsx] First client.businessName:', clientList[0].businessName);
        console.log('🔧 [clients.tsx] First client.business_name:', (clientList[0] as any).business_name);
      }
      return clientList;
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: InsertClient) => {
      const res = await apiRequest("POST", "/api/clients", data);
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Unknown error', message: 'Nepodarilo sa vytvoriť klienta.' }));
        throw new Error(errorData.message || errorData.error || 'Nepodarilo sa vytvoriť klienta.');
      }
      return res.json();
    },
    onSuccess: () => {
      setIsCreateDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: t('clients:messages.created'), description: t('clients:messages.createSuccess') });
    },
    onError: (error: Error) => {
      console.error('🔧 [clients.tsx] Create error:', error);
      toast({
        title: t('common:error'),
        description: error.message || 'Nepodarilo sa vytvoriť klienta.',
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: InsertClient }) => {
      console.log('🔧 [clients.tsx] updateMutation data:', data);
      console.log('🔧 [clients.tsx] updateMutation id:', id);
      const res = await apiRequest("PATCH", `/api/clients/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      console.log('🔧 [clients.tsx] Update successful, invalidating queries');
      setEditingClient(null);
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      toast({ title: t('clients:messages.updated'), description: t('clients:messages.updateSuccess') });
    },
    onError: (error: Error) => {
      console.error('🔧 [clients.tsx] Update error:', error);
      toast({
        title: t('common:error'),
        description: error.message,
        variant: "destructive",
      });
    },
    // Vypnúť cache pre mutation
    staleTime: 0,
    cacheTime: 0,
  });
  
  const { data: tasksCount } = useQuery<{ count: number }>({
    queryKey: ["/api/clients", deletingClient?.id, "tasks-count"],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/clients/${deletingClient!.id}/tasks-count`);
      return res.json();
    },
    enabled: !!deletingClient?.id,
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      await apiRequest("DELETE", `/api/clients/${id}`);
    },
    onSuccess: () => {
      console.log('🔧 [clients.tsx] Delete successful, invalidating queries');
      setDeletingClient(null); // Close dialog
      queryClient.invalidateQueries({ queryKey: ["/api/clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/tasks"] }); // Refresh tasks as they may depend on clients
      toast({ 
        title: t("clients:messages.deleteSuccess"), 
        description: t("clients:messages.deleteDescription") 
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("common:info"),
        description: error.message,
        variant: "default",
      });
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="page-container min-w-0">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 section-spacing">
        <div>
          <h1 className="page-title">{t('clients:title')}</h1>
          <p className="text-muted-foreground text-lg">{t('clients:description')}</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              {t('clients:create')}
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>{t('clients:create')}</DialogTitle>
              <DialogDescription>
                {t('clients:createDescription')}
              </DialogDescription>
            </DialogHeader>
            <ClientForm 
              onSubmit={async (data) => {
                try {
                  await createMutation.mutateAsync(data);
                  // Okno sa zatvorí v onSuccess callback
                } catch (error) {
                  // Error sa spracuje v onError callback
                }
              }} 
              isSubmitting={createMutation.isPending}
              onClose={() => setIsCreateDialogOpen(false)}
            />
          </DialogContent>
        </Dialog>
      </div>

      {editingClient && (
        <Dialog open={!!editingClient} onOpenChange={(open) => !open && setEditingClient(null)}>
          <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
            <DialogHeader>
              <DialogTitle>{t('clients.editClient', 'Upraviť klienta')}</DialogTitle>
              <DialogDescription>
                {t('clients.editClientDescription', 'Upravte údaje existujúceho klienta.')}
              </DialogDescription>
            </DialogHeader>
            <ClientForm 
              onSubmit={async (data) => {
                try {
                  await updateMutation.mutateAsync({ id: editingClient.id, data });
                  // Okno sa zatvorí v onSuccess callback
                } catch (error) {
                  // Error sa spracuje v onError callback
                }
              }} 
              defaultValues={editingClient} 
              isSubmitting={updateMutation.isPending}
              onClose={() => setEditingClient(null)}
            />
          </DialogContent>
        </Dialog>
      )}

      {error ? (
        <div className="rounded-md bg-destructive/15 p-4 text-destructive flex items-center">
          <AlertTriangle className="h-4 w-4 mr-2" />
          {t('clients:error')}
        </div>
      ) : clients && clients.length > 0 ? (
        <div className="space-y-4">
          {/* Search and Sort Controls – responzívne */}
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 bg-card p-3 sm:p-4 rounded-lg border">
            <div className="flex-1 min-w-0">
              <Input
                id="client-search"
                name="client-search"
                type="search"
                autoComplete="off"
                placeholder={t('clients:search.placeholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full min-w-0 bg-background border-input"
              />
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as "name" | "email" | "hourlyRate")}
                className="min-w-0 px-3 py-2 border rounded-md bg-background border-input text-sm"
              >
                <option value="name">{t('clients:search.sortByName')}</option>
                <option value="email">{t('clients:search.sortByEmail')}</option>
                <option value="hourlyRate">{t('clients:search.sortByRate')}</option>
              </select>
              <Button
                variant="outline"
                size="sm"
                className="shrink-0"
                onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              >
                {sortOrder === "asc" ? "↑" : "↓"}
              </Button>
            </div>
          </div>
          
          <div className="table-container w-full min-w-0">
            <div className="w-full overflow-x-auto min-w-0">
            <Table className="tk-row w-full min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>{t('clients:table.name')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('clients:table.email')}</TableHead>
                  <TableHead className="hidden md:table-cell">{t('clients:table.phone')}</TableHead>
                  <TableHead className="text-right">{t('clients:table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.isArray(clients) ? clients
                  .filter(client => client && typeof client === 'object' && client.name)
                  .filter(client => 
                    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    (client.businessName && client.businessName.toLowerCase().includes(searchTerm.toLowerCase()))
                  )
                  .sort((a, b) => {
                    let aValue = a[sortBy];
                    let bValue = b[sortBy];
                    
                    if (sortBy === "hourlyRate") {
                      const numA = Number(aValue) || 0;
                      const numB = Number(bValue) || 0;
                      return sortOrder === "asc" ? numA - numB : numB - numA;
                    } else {
                      const strA = String(aValue).toLowerCase();
                      const strB = String(bValue).toLowerCase();
                      return sortOrder === "asc" 
                        ? (strA > strB ? 1 : -1)
                        : (strA < strB ? 1 : -1);
                    }

                  })
                  .map((client) => (
                  <TableRow key={client.id}>
                    <TableCell className="font-medium">
                      <div>
                        {client.name}
                        <div className="md:hidden text-sm text-muted-foreground mt-1">
                          <span className="mobile-data-label">{t('clients:mobile.email')}</span> {client.email}
                        </div>
                        {client.phone && (
                          <div className="md:hidden text-sm text-muted-foreground">
                            <span className="mobile-data-label">{t('clients:mobile.phone')}</span> {client.phone}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{client.email}</TableCell>
                    <TableCell className="hidden md:table-cell">{client.phone || "-"}</TableCell>
                                    <TableCell className="text-right">
                                      <div className="flex flex-wrap justify-end gap-1 sm:gap-2">
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                size="icon"
                                variant="ghost"
                                onClick={() => window.open(`/shared-client-tasks/${client.id}?token=shared-${client.id}-${Date.now()}`, "_blank")}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-100 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/40"
                              >
                                <Share className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{t('clients:tooltips.viewTasks')}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="text-green-600 hover:text-green-800 hover:bg-green-100 dark:text-green-400 dark:hover:text-green-300 dark:hover:bg-green-900/40"
                            >
                              <Share className="h-4 w-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="sm:max-w-md bg-background/80 backdrop-blur-xl shadow-2xl border-l-4 border-l-primary/30">
                            <DialogHeader>
                              <DialogTitle>{t('clients:sharing.title')}</DialogTitle>
                              <DialogDescription>
                                {t('clients:sharing.description')} {client.name}.
                              </DialogDescription>
                            </DialogHeader>
                            
                            <div className="space-y-4 py-4">
                              <div className="space-y-2">
                                <div className="font-medium text-sm">
                                  {t('clients:sharing.info')}
                                </div>
                                <div className="flex flex-col xs:flex-row gap-2 items-stretch xs:items-center">
                                  <Input
                                    readOnly
                                    value={`${window.location.origin}/shared-client-tasks/${client.id}?token=shared-${client.id}-${Date.now()}`}
                                    className="flex-1 min-w-0 text-sm"
                                  />
                                  <Button
                                    size="sm"
                                    className="shrink-0"
                                    onClick={() => {
                                      navigator.clipboard.writeText(`${window.location.origin}/shared-client-tasks/${client.id}?token=shared-${client.id}-${Date.now()}`);
                                      toast({
                                        title: t("clients:sharing.linkCopied"),
                                        description: t("clients:sharing.linkCopiedDescription")
                                      });
                                    }}
                                  >
                                    {t("clients:sharing.copyLink")}
                                  </Button>
                                </div>
                                <div className="text-sm text-muted-foreground">
                                  {t("clients:sharing.accessInfo")}
                                </div>
                              </div>
                              
                              <div className="border-t pt-4 mt-4">
                                <div className="font-medium text-sm mb-2">
                                  {t("clients:sharing.sendEmail")}
                                </div>
                                <div className="grid grid-cols-1 gap-2">
                                  <Input
                                    placeholder={t("clients:sharing.emailPlaceholder")}
                                    defaultValue={client.email || ""}
                                    className="w-full"
                                  />
                                  <Button 
                                    className="w-full flex items-center"
                                    variant="outline"
                                    onClick={async () => {
                                      const emailInput = document.querySelector('input[placeholder*="email"]') as HTMLInputElement;
                                      const email = emailInput?.value || client.email;
                                      
                                      if (!email) {
                                        toast({
                                          title: t("common:error"),
                                          description: "Email je povinný",
                                          variant: "destructive"
                                        });
                                        return;
                                      }
                                      
                                      try {
                                        const response = await fetch(`/api/clients/${client.id}/send-tasks-email`, {
                                          method: 'POST',
                                          headers: {
                                            'Content-Type': 'application/json',
                                          },
                                          credentials: 'include',
                                          body: JSON.stringify({ email })
                                        });
                                        
                                        if (!response.ok) {
                                          const error = await response.json();
                                          throw new Error(error.error || 'Failed to send email');
                                        }
                                        
                                        toast({
                                          title: t("clients:sharing.emailSent"),
                                          description: t("clients:sharing.emailSentDescription")
                                        });
                                      } catch (error: any) {
                                        toast({
                                          title: t("common:error"),
                                          description: error.message || "Nepodarilo sa odoslať email",
                                          variant: "destructive"
                                        });
                                      }
                                    }}
                                  >
                                    <Mail className="h-4 w-4 mr-2" />
                                    {t("clients:sharing.sendEmailButton")}
                                  </Button>
                                </div>
                                <div className="text-xs text-muted-foreground mt-2">
                                  {t("clients:sharing.emailInfo")}
                                </div>
                              </div>
                            </div>
                          </DialogContent>
                        </Dialog>
                        <Button variant="ghost" size="icon" onClick={() => {
                          console.log('🔧 [clients.tsx] Setting editingClient:', client);
                          console.log('🔧 [clients.tsx] Client keys:', Object.keys(client));
                          console.log('🔧 [clients.tsx] Client JSON:', JSON.stringify(client, null, 2));
                          setEditingClient(client);
                        }} title={t('buttons:edit')}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          title={t('buttons:delete')}
                          onClick={() => setDeletingClient(client)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : null}
              </TableBody>
            </Table>
          </div>
        </div>
        </div>
      ) : (
        <div className="text-center p-8 text-muted-foreground border rounded-md bg-card">
          {t('clients:noClients')}
        </div>
      )}

      {deletingClient && (
        <Dialog open={!!deletingClient} onOpenChange={(open) => !open && setDeletingClient(null)}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>{t("clients:delete.title")}</DialogTitle>
              <DialogDescription>
                {t("clients:delete.warning")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {tasksCount && tasksCount.count > 0 && (
                <div className="flex items-center gap-2 rounded-lg border border-amber-500/50 bg-amber-500/10 p-3 text-amber-700 dark:text-amber-400">
                  <AlertTriangle className="h-5 w-5 shrink-0" />
                  <p>
                    {t("clients:delete.hasTasks", { count: tasksCount.count }) ||
                      `Tento klient má ${tasksCount.count} ${tasksCount.count === 1 ? "úlohu" : tasksCount.count < 5 ? "úlohy" : "úloh"}. Naozaj ho chcete vymazať?`}
                  </p>
                </div>
              )}
              <div className="flex items-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <p>{t("clients:delete.confirmation")}</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button 
                  variant="outline"
                  onClick={() => setDeletingClient(null)}
                >
                  {t("clients:actions.cancel")}
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={() => {
                    deleteMutation.mutate(deletingClient.id);
                  }}
                  disabled={deleteMutation.isPending}
                >
                  {deleteMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    t("clients:actions.delete")
                  )}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
